import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { modifier } from 'ember-modifier';
import type Owner from '@ember/owner';
import type RouterService from '@ember/routing/router-service';
import Modal from 'tio-ui/components/modal';
import Button from 'tio-ui/components/button';
import { PhMinus } from 'ember-phosphor-icons';
import { not } from 'tio-ui/utilities';
import type { DemoStoryId } from 'tio-employee/demo/stories';
import type DemoModeService from 'tio-employee/services/demo-mode';
import type BetaExperienceService from 'tio-employee/services/beta-experience';
import {
  stripDemoParamsAfterPersist,
  isDemoDebtLevelCompatible,
  normalizeDemoDebtLevel,
  type DemoClientId,
  type DemoProductId,
  type DemoSessionRoleId,
  type DemoTaPaymentModelId,
  type DemoTasScenarioId,
  type DemoDebtLevelId,
  type DemoDocumentStatusId,
  type DemoUserScenarioId,
} from 'tio-employee/utils/career-pathways-demo-data';

interface DemoDataShortcutSignature {
  Args: Record<string, never>;
}

export default class DemoDataShortcut extends Component<DemoDataShortcutSignature> {
  @service declare demoMode: DemoModeService;
  @service('beta-experience') declare betaExperience: BetaExperienceService;
  @service declare router: RouterService;

  @tracked isOpen = false;
  @tracked isMinimized = false;
  @tracked selectedStoryId: DemoStoryId | null = null;
  @tracked isStoryHandoffOpen = false;
  #lastStoryHandoff: string | null = null;

  toggleMinimized = () => {
    this.isMinimized = !this.isMinimized;
  };

  promptStoryHandoff = () => {
    const story = this.activeStory;
    const receipt = this.router.currentRoute?.queryParams?.['applicationSubmitted'];
    const action = this.storyAction;
    if (action?.kind === 'ta-review' && !this.router.currentRoute?.name?.endsWith('.manage'))
      return;
    if (
      !story ||
      !this.#canAutoPromptStoryHandoff ||
      this.isOpen ||
      receipt === true ||
      receipt === 'true'
    )
      return;
    const key = `${story.id}:${story.stage}:${this.storyAction?.kind}:${this.demoMode.currentSelection.roleId}`;
    if (this.#lastStoryHandoff === key) return;
    this.#lastStoryHandoff = key;
    this.isStoryHandoffOpen = true;
  };

  get stories() {
    return this.demoMode.stories;
  }

  get activeStory() {
    return this.routeRevision >= 0 ? this.demoMode.story : null;
  }

  get activeStoryName() {
    return this.stories.find((story) => story.id === this.activeStory?.id)?.name;
  }

  get storyAction() {
    return this.routeRevision >= 0 ? this.demoMode.storyAction : null;
  }

  get storyHandoffAvailable() {
    return Boolean(this.activeStory && this.storyAction);
  }

  get #canAutoPromptStoryHandoff() {
    const kind = this.storyAction?.kind;
    return Boolean(this.activeStory && kind !== 'pslf-review' && kind?.match(/review|return/));
  }

  get isStoryPslfComplete() {
    const forms = this.activeStory?.pslfForms ?? [];
    return forms.length > 0 && forms.every((form) => form.status === 'submitted');
  }

  selectStory = (id: DemoStoryId) => {
    this.selectedStoryId = id;
  };

  customizeDemo = () => {
    this.selectedStoryId = null;
  };

  openStoryHandoff = () => {
    this.isStoryHandoffOpen = true;
  };

  closeStoryHandoff = () => {
    this.isStoryHandoffOpen = false;
  };

  confirmStoryHandoff = () => {
    this.isStoryHandoffOpen = false;
    this.demoMode.continueStory();
  };

  /**
   * Bumped on every route transition (see trackRouteChanges) so the pill
   * getters recompute: demo activation is PATH-scoped but read from
   * `window.location`, which Ember does not track.
   */
  @tracked routeRevision = 0;
  @tracked selectedClientId: DemoClientId;
  @tracked selectedProductIds: DemoProductId[] = [];
  @tracked selectedRoleId: DemoSessionRoleId;
  @tracked selectedUserScenarioId: DemoUserScenarioId;
  @tracked selectedTaPaymentModelId: DemoTaPaymentModelId;
  /**
   * null = Automatic (the persona-derived TA scenario). Deliberately resets to
   * Automatic on every open: storage can't distinguish an explicit pick from a
   * derived one after apply, and a pick that silently outlived its persona
   * would stop the user-scenario cards from steering the TA dashboard.
   */
  @tracked selectedTasScenarioId: DemoTasScenarioId | null = null;
  @tracked selectedDebtLevelId: DemoDebtLevelId;
  @tracked selectedDocumentStatusId: DemoDocumentStatusId;

  constructor(owner: Owner, args: DemoDataShortcutSignature['Args']) {
    super(owner, args);

    const selection = this.demoMode.currentSelection;
    this.selectedClientId = selection.clientId;
    this.selectedProductIds = [...selection.productIds];
    this.selectedRoleId = selection.roleId;
    this.selectedUserScenarioId = selection.userScenarioId;
    this.selectedTaPaymentModelId = selection.taPaymentModelId;
    this.selectedDebtLevelId = normalizeDemoDebtLevel(
      selection.userScenarioId,
      selection.debtLevelId
    );
    this.selectedDocumentStatusId = selection.documentStatusId;
  }

  /**
   * Installs the global Ctrl+Shift+~ listener for the lifetime of the host
   * element, registers this picker's opener on the service (so launchers like
   * the reimagined header's "Demo settings" button open THE one picker), and
   * runs the one-shot post-boot URL cleanup for sticky sessions. Lives in a
   * modifier (not the constructor) per repo convention and is only ever
   * rendered when demo mode is available, so none of it attaches in a session
   * that cannot use it.
   */
  installShortcut = modifier(() => {
    const handler = (event: KeyboardEvent) => {
      if (!this.isDemoShortcut(event) || this.isEditableTarget(event.target)) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      this.openModal();
    };

    document.addEventListener('keydown', handler);
    const unregisterOpener = this.demoMode.registerPickerOpener(this.openModal);

    // A sticky (picker-applied) session's entry URL may still carry demo
    // params — persist them, then wipe the address bar so a copied URL is
    // clean. No-op for pure ?demo=true views (there the params ARE the
    // session) and outside demo entirely.
    stripDemoParamsAfterPersist();

    return () => {
      document.removeEventListener('keydown', handler);
      unregisterOpener();
    };
  });

  /**
   * Invalidates the pill state on every route transition for the lifetime of
   * the host element (the demo-active/paused split depends on the current
   * path — see routeRevision).
   */
  trackRouteChanges = modifier(() => {
    const promptWhenReady = () => {
      if (!this.#canAutoPromptStoryHandoff) return;
      const openDialog = Array.from(document.querySelectorAll('[role="dialog"]')).some(
        (dialog) => dialog.getClientRects().length > 0
      );
      if (!openDialog) this.promptStoryHandoff();
      if (this.isStoryHandoffOpen) observer.disconnect();
    };
    const bump = () => {
      this.routeRevision++;
      observer.disconnect();
      if (this.#canAutoPromptStoryHandoff) {
        observer.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['style', 'hidden', 'aria-hidden'],
        });
      }
      promptWhenReady();
    };
    const observer = new MutationObserver(promptWhenReady);
    this.router.on('routeDidChange', bump);
    window.addEventListener('demo-story-changed', bump);
    return () => {
      this.router.off('routeDidChange', bump);
      window.removeEventListener('demo-story-changed', bump);
      observer.disconnect();
    };
  });

  /**
   * Whether the applied demo session renders on the current route — drives the
   * active demo pill. `routeRevision >= 0` is always true; reading it makes
   * this getter recompute per transition.
   */
  get isDemoActive() {
    return this.routeRevision >= 0 && this.demoMode.isActive;
  }

  /**
   * A sticky demo session is armed but the current route is not demoable, so
   * the page is showing REAL data — drives the paused-pill warning state.
   */
  get isDemoPaused() {
    return this.routeRevision >= 0 && !this.demoMode.isActive && this.demoMode.isArmed;
  }

  /** There is a demo session to exit (active here or armed elsewhere). */
  get canExitDemo() {
    return this.isDemoActive || this.isDemoPaused;
  }

  /**
   * The APPLIED session's persona for the pill label — unlike
   * selectedClientName, which tracks the modal's in-flight (unapplied)
   * selection.
   */
  get activePersonaName() {
    return this.demoMode.clientName(this.demoMode.currentSelection.clientId);
  }

  /**
   * Whether this demo is rendering beta surfaces. Beta opt-in carries into a
   * demo, so the pill has to say so — otherwise a machine left opted in shows
   * unreleased surfaces in a customer demo with nothing to give it away.
   */
  get isShowingBeta() {
    return this.betaExperience.isEnabled;
  }

  exitDemo = () => {
    this.demoMode.exit();
  };

  resumeDemo = () => {
    this.demoMode.resume();
  };

  get clients() {
    return this.demoMode.clients;
  }

  get products() {
    return this.demoMode.products;
  }

  get roles() {
    return this.demoMode.roles;
  }

  get userScenarios() {
    return this.demoMode.userScenarios;
  }

  get taPaymentModels() {
    return this.demoMode.taPaymentModels;
  }

  /** The Automatic card (null id) plus the real scenarios — one loop in the template. */
  get tasScenarioOptions(): { id: DemoTasScenarioId | null; name: string; description: string }[] {
    return [
      { id: null, name: 'Automatic', description: 'Matches the selected user scenario' },
      ...this.demoMode.tasScenarios,
    ];
  }

  get debtLevels() {
    return this.demoMode.debtLevels;
  }

  get documentStatuses() {
    return this.demoMode.documentStatuses;
  }

  get selectedClientName() {
    return this.demoMode.clientName(this.selectedClientId);
  }

  get selectedProductNames() {
    return this.selectedProductIds.map((id) => this.demoMode.productName(id)).join(', ');
  }

  get selectedRoleName() {
    return this.demoMode.roleName(this.selectedRoleId);
  }

  get selectedUserScenarioName() {
    return this.demoMode.userScenarioName(this.selectedUserScenarioId);
  }

  get selectedTaPaymentModelName() {
    return this.demoMode.taPaymentModelName(this.selectedTaPaymentModelId);
  }

  /** Summary-line name; null on Automatic or whenever the apply path would drop the pick. */
  get selectedTasScenarioName() {
    return this.isTaEmployeeExperience && this.selectedTasScenarioId
      ? this.demoMode.tasScenarioName(this.selectedTasScenarioId)
      : null;
  }

  get selectedDebtLevelName() {
    return this.demoMode.debtLevelName(this.selectedDebtLevelId);
  }

  get debtScenarioNote() {
    if (this.selectedUserScenarioId === 'first-login') {
      return 'First Login has no linked loans. Choose another user scenario to show a loan balance.';
    }
    if (
      this.selectedUserScenarioId === 'power-user' ||
      this.selectedUserScenarioId === 'benefits-maxed'
    ) {
      return `${this.selectedUserScenarioName} requires linked loans. Choose First Login or New Employee to show no linked loans.`;
    }
    return null;
  }

  isDebtLevelDisabled = (debtLevelId: DemoDebtLevelId) => {
    return !isDemoDebtLevelCompatible(this.selectedUserScenarioId, debtLevelId);
  };

  isTasScenarioDisabled = (tasScenarioId: DemoTasScenarioId | null) => {
    return (
      this.selectedUserScenarioId === 'first-login' &&
      tasScenarioId !== null &&
      tasScenarioId !== 'not-started'
    );
  };

  get selectedDocumentStatusName() {
    return this.demoMode.documentStatusName(this.selectedDocumentStatusId);
  }

  // Internal-only derived state (used by isEmployeeExperience, not the template).
  private get selectedRole() {
    return this.roles.find((role) => role.id === this.selectedRoleId);
  }

  /**
   * Whether the selected role simulates the employee/participant experience.
   * The "user scenario" cards are employee-journey personas (First Login,
   * New Employee, Power User, Benefits Maxed, …), so they only make sense for
   * the employee. The Supervisor (approver) and Admin (partner-admin) views have
   * no employee benefits journey to pick — forcing a scenario choice there is
   * incoherent, so the section is hidden for those roles.
   */
  get isEmployeeExperience() {
    return this.selectedRole?.relationshipType === 'PARTICIPANT';
  }

  /**
   * Whether Tuition Assistance is among the selected products. The "TA payment
   * model" cards configure TA-only behavior (the TAS assistance manner and the
   * direct-pay/card-review settings), which has no surface unless TA is selected
   * — so the section is hidden otherwise, mirroring the employee-only
   * user-scenario section above. The apply path coerces the persisted model back
   * to the default when TA is not selected, so a stale value never rides along.
   */
  get isTaProduct() {
    return this.demoMode.isTaSelected(this.selectedProductIds);
  }

  /**
   * Whether the TA-scenario cards apply: the axis pins the EMPLOYEE TA
   * dashboard state, so it needs both Tuition Assistance selected (like the
   * payment model) and the employee experience (like the user scenarios) —
   * the Supervisor/Admin surfaces never read it.
   */
  get isTaEmployeeExperience() {
    return this.isTaProduct && this.isEmployeeExperience;
  }

  /** A product is included in the multi-select set. */
  isProductSelected = (productId: DemoProductId) => {
    return this.selectedProductIds.includes(productId);
  };

  /**
   * Whether the selected persona's employer is PSLF-eligible (non-profit or
   * government). For-profit personas can't qualify for PSLF.
   */
  get isPslfAvailable() {
    return this.demoMode.isPslfEligibleClient(this.selectedClientId);
  }

  /**
   * Products the selected persona makes unavailable — the responsive guard. PSLF
   * is disabled for a for-profit persona (it can never qualify), so the option
   * greys out instead of silently producing an impossible combination.
   */
  isProductDisabled = (productId: DemoProductId) => {
    return productId === 'pslf' && !this.isPslfAvailable;
  };

  /** At least one benefit must be selected to apply a coherent demo session. */
  get canApply() {
    return this.selectedProductIds.length > 0;
  }

  private isDemoShortcut(event: KeyboardEvent) {
    return event.ctrlKey && event.shiftKey && (event.key === '~' || event.code === 'Backquote');
  }

  private isEditableTarget(target: EventTarget | null) {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    return (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement ||
      target.isContentEditable ||
      !!target.closest('[contenteditable=""], [contenteditable="true"]')
    );
  }

  openModal = () => {
    const selection = this.demoMode.currentSelection;
    this.selectedClientId = selection.clientId;
    this.selectedProductIds = [...selection.productIds];
    this.selectedRoleId = selection.roleId;
    this.selectedUserScenarioId = selection.userScenarioId;
    this.selectedTaPaymentModelId = selection.taPaymentModelId;
    this.selectedTasScenarioId = null;
    this.selectedDebtLevelId = normalizeDemoDebtLevel(
      selection.userScenarioId,
      selection.debtLevelId
    );
    this.selectedDocumentStatusId = selection.documentStatusId;
    this.selectedStoryId = this.activeStory?.id ?? null;
    this.isOpen = true;
  };

  closeModal = () => {
    this.isOpen = false;
  };

  selectClient = (clientId: DemoClientId) => {
    this.selectedClientId = clientId;
    // Switching to a for-profit persona makes PSLF unavailable — drop it so the
    // applied set matches what the picker now allows (the option also greys out).
    if (!this.demoMode.isPslfEligibleClient(clientId)) {
      this.selectedProductIds = this.selectedProductIds.filter((id) => id !== 'pslf');
    }
  };

  selectProduct = (productId: DemoProductId) => {
    if (this.isProductDisabled(productId)) {
      return;
    }
    // Toggle membership — products are a multi-select set.
    this.selectedProductIds = this.selectedProductIds.includes(productId)
      ? this.selectedProductIds.filter((id) => id !== productId)
      : [...this.selectedProductIds, productId];
  };

  selectRole = (roleId: DemoSessionRoleId) => {
    this.selectedRoleId = roleId;
  };

  selectUserScenario = (userScenarioId: DemoUserScenarioId) => {
    this.selectedUserScenarioId = userScenarioId;
    this.selectedDebtLevelId = normalizeDemoDebtLevel(userScenarioId, this.selectedDebtLevelId);
    this.selectedTasScenarioId = null;
  };

  selectTaPaymentModel = (taPaymentModelId: DemoTaPaymentModelId) => {
    this.selectedTaPaymentModelId = taPaymentModelId;
  };

  selectTasScenario = (tasScenarioId: DemoTasScenarioId | null) => {
    if (this.isTasScenarioDisabled(tasScenarioId)) {
      return;
    }
    this.selectedTasScenarioId = tasScenarioId;
  };

  selectDebtLevel = (debtLevelId: DemoDebtLevelId) => {
    if (!this.isEmployeeExperience || this.isDebtLevelDisabled(debtLevelId)) {
      return;
    }
    this.selectedDebtLevelId = debtLevelId;
  };

  selectDocumentStatus = (documentStatusId: DemoDocumentStatusId) => {
    this.selectedDocumentStatusId = documentStatusId;
  };

  apply = () => {
    if (this.selectedStoryId) {
      this.demoMode.startStory(this.selectedStoryId);
      return;
    }
    if (!this.canApply) {
      return;
    }
    this.demoMode.apply({
      clientId: this.selectedClientId,
      productIds: this.selectedProductIds,
      roleId: this.selectedRoleId,
      userScenarioId: this.selectedUserScenarioId,
      taPaymentModelId: this.selectedTaPaymentModelId,
      // A pick made while the cards were hidden must not ride along (the
      // resolver guards this too, on the same participant+TA condition).
      tasScenarioId: this.isTaEmployeeExperience
        ? (this.selectedTasScenarioId ?? undefined)
        : undefined,
      debtLevelId: this.isEmployeeExperience ? this.selectedDebtLevelId : undefined,
      documentStatusId: this.selectedDocumentStatusId,
    });
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    {{#if this.demoMode.isAvailable}}
      <div hidden {{this.installShortcut}} {{this.trackRouteChanges}}></div>

      {{#if this.isDemoActive}}
        {{! Always-visible demo indicator: the rep can see they're in demo
            mode on EVERY demoable route (reimagined AND admin), jump to the
            picker, or exit in one click. }}
        <div
          class="tio-demo-pill {{if this.activeStory 'tio-demo-pill--story'}}"
          data-test-demo-pill
        >
          {{#unless this.isMinimized}}
            <button
              type="button"
              class="tio-demo-pill-main"
              title="Demo settings (Ctrl+Shift+~)"
              data-test-demo-pill-settings
              {{on "click" this.openModal}}
            >
              <span class="tio-demo-pill-dot" aria-hidden="true"></span>
              Demo &middot;
              {{if this.activeStoryName this.activeStoryName this.activePersonaName}}{{#if
                this.isShowingBeta
              }}
                &middot;
                <span data-test-demo-pill-beta>Beta</span>
              {{/if}}
            </button>
            {{#if
              (and
                (eq this.activeStory.id "non-profit-pslf-ta")
                this.isStoryPslfComplete
                (eq this.activeStory.stage "draft")
                (eq this.demoMode.currentSelection.roleId "employee")
              )
            }}
              <Button
                @route="authenticated.tas.dashboard"
                @appearance="minimal"
                @intent="default"
                @testHook="demo-story__pslf-progress"
              >Continue to TA</Button>
            {{/if}}
            {{#if this.storyHandoffAvailable}}
              <Button
                @appearance="outlined"
                @intent="default"
                @testHook="demo-story__open-handoff"
                data-test-demo-story-handoff-open
                {{on "click" this.openStoryHandoff}}
              >
                {{this.storyAction.label}}
              </Button>
            {{/if}}
          {{/unless}}
          <Button
            @appearance="minimal"
            @intent="default"
            @size="sm"
            @ariaLabel={{if this.isMinimized "Expand demo badge" "Minimize demo badge"}}
            @testHook="demo-pill__minimize-toggle"
            title={{if this.isMinimized "Expand demo badge" "Minimize demo badge"}}
            aria-expanded={{if this.isMinimized "false" "true"}}
            data-test-demo-pill-minimize-toggle
            {{on "click" this.toggleMinimized}}
          >
            {{#if this.isMinimized}}
              Demo
            {{else}}
              <PhMinus @size={{16}} />
            {{/if}}
          </Button>
          {{#unless this.isMinimized}}
            <button
              type="button"
              class="tio-demo-pill-exit"
              aria-label="Exit demo mode"
              title="Exit demo mode"
              data-test-demo-pill-exit
              {{on "click" this.exitDemo}}
            >
              &times;
            </button>
          {{/unless}}
        </div>
      {{else if this.isDemoPaused}}
        {{! Armed session on a non-demoable route: the page is showing REAL
            data. Warn instead of disappearing silently. }}
        <div class="tio-demo-pill tio-demo-pill--paused" data-test-demo-pill-paused>
          <span class="tio-demo-pill-label">Demo paused &mdash; showing real data</span>
          <button
            type="button"
            class="tio-demo-pill-action"
            data-test-demo-pill-resume
            {{on "click" this.resumeDemo}}
          >
            Return to demo
          </button>
          <button
            type="button"
            class="tio-demo-pill-exit"
            aria-label="Exit demo mode"
            title="Exit demo mode"
            data-test-demo-pill-exit
            {{on "click" this.exitDemo}}
          >
            &times;
          </button>
        </div>
      {{/if}}

      <Modal
        @isOpen={{this.isOpen}}
        @onClose={{this.closeModal}}
        @size="2xl"
        @ariaLabel="Demo data options"
        data-test-demo-data-modal
      >
        <:header>
          <div>
            <h2 class="tio-text-lg-semibold tio-fg-primary">Demo data</h2>
            <p class="mt-1 text-sm tio-fg-muted">
              Choose a ready-to-present Story, or configure individual demo options.
            </p>
          </div>
        </:header>

        <:body>
          <div class="space-y-8">
            <section data-test-demo-stories-section>
              <h3 class="tio-text-sm-semibold uppercase tracking-wide tio-fg-muted">Stories</h3>
              <p class="mt-1 tio-text-sm-normal tio-fg-muted">One preset, one happy path. Employee,
                approver, and back — all options are set for you.</p>
              <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {{#each this.stories as |story|}}
                  <Button
                    @appearance="outlined"
                    @intent="default"
                    class="tio-option-card tio-option-card--compact tio-option-card--accent tio-accent-brand
                      {{if (eq story.id this.selectedStoryId) 'tio-option-card--selected'}}"
                    aria-pressed={{if (eq story.id this.selectedStoryId) "true" "false"}}
                    @testHook={{story.id}}
                    data-test-demo-story={{story.id}}
                    {{on "click" (fn this.selectStory story.id)}}
                  >
                    <span class="tio-col-tight text-left">
                      <span class="tio-text-base-semibold tio-fg-primary">{{story.name}}</span>
                      <span class="tio-text-sm-normal tio-fg-muted">{{story.description}}</span>
                    </span>
                  </Button>
                {{/each}}
              </div>
              {{#if this.selectedStoryId}}
                <p class="mt-3 tio-text-sm-normal tio-fg-muted" data-test-demo-story-ready>
                  Start at the benefit dashboard and complete the normal employee flow. The Demo
                  pill guides role changes and simulated processing. Starting again resets this
                  story's progress.
                </p>
                <Button
                  @appearance="minimal"
                  @intent="default"
                  class="mt-3"
                  @testHook="demo-story__customize"
                  data-test-demo-story-customize
                  {{on "click" this.customizeDemo}}
                >Customize individual options</Button>
              {{/if}}
            </section>
            {{#unless this.selectedStoryId}}
              <section>
                <h3 class="tio-text-sm-semibold uppercase tracking-wide tio-fg-muted">Persona</h3>
                <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {{#each this.clients as |client|}}
                    <button
                      type="button"
                      class="tio-option-card tio-option-card--compact tio-option-card--accent tio-accent-brand
                        {{if (eq client.id this.selectedClientId) 'tio-option-card--selected'}}"
                      aria-pressed={{if (eq client.id this.selectedClientId) "true" "false"}}
                      data-test-demo-client={{client.id}}
                      {{on "click" (fn this.selectClient client.id)}}
                    >
                      <div class="font-semibold tio-fg-primary">
                        {{if client.personaLabel client.personaLabel client.industry}}
                      </div>
                      <div class="mt-1 text-sm tio-fg-muted">{{client.name}}</div>
                      <div class="mt-2 text-xs tio-fg-disabled">
                        {{client.employeeCount}}
                        employees &middot;
                        {{client.taUtilization}}% TA utilization
                      </div>
                    </button>
                  {{/each}}
                </div>
              </section>

              <section>
                <h3 class="tio-text-sm-semibold uppercase tracking-wide tio-fg-muted">
                  Products
                </h3>
                <p class="mt-1 text-xs tio-fg-disabled">Select any combination of benefits.</p>
                <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {{#each this.products as |product|}}
                    <button
                      type="button"
                      disabled={{this.isProductDisabled product.id}}
                      class="tio-option-card tio-option-card--compact tio-option-card--accent tio-accent-success
                        {{if (this.isProductSelected product.id) 'tio-option-card--selected'}}"
                      role="checkbox"
                      aria-checked={{if (this.isProductSelected product.id) "true" "false"}}
                      aria-disabled={{if (this.isProductDisabled product.id) "true" "false"}}
                      data-test-demo-product={{product.id}}
                      {{on "click" (fn this.selectProduct product.id)}}
                    >
                      <div class="font-semibold tio-fg-primary">{{product.name}}</div>
                      <div class="mt-1 text-sm tio-fg-muted">{{product.description}}</div>
                      {{#if (this.isProductDisabled product.id)}}
                        <div class="mt-1 text-xs tio-fg-disabled">
                          Requires a non-profit or government employer
                        </div>
                      {{/if}}
                    </button>
                  {{/each}}
                </div>
              </section>

              <section>
                <h3 class="tio-text-sm-semibold uppercase tracking-wide tio-fg-muted">Role</h3>
                <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {{#each this.roles as |role|}}
                    <button
                      type="button"
                      class="tio-option-card tio-option-card--compact tio-option-card--accent tio-accent-info
                        {{if (eq role.id this.selectedRoleId) 'tio-option-card--selected'}}"
                      aria-pressed={{if (eq role.id this.selectedRoleId) "true" "false"}}
                      data-test-demo-role={{role.id}}
                      {{on "click" (fn this.selectRole role.id)}}
                    >
                      <div class="font-semibold tio-fg-primary">{{role.name}}</div>
                      <div class="mt-1 text-sm tio-fg-muted">{{role.description}}</div>
                    </button>
                  {{/each}}
                </div>
              </section>

              {{#if this.isEmployeeExperience}}
                <section>
                  <h3 class="tio-text-sm-semibold uppercase tracking-wide tio-fg-muted">
                    User scenario
                  </h3>
                  <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {{#each this.userScenarios as |scenario|}}
                      <button
                        type="button"
                        class="tio-option-card tio-option-card--compact tio-option-card--accent tio-accent-warning
                          {{if
                            (eq scenario.id this.selectedUserScenarioId)
                            'tio-option-card--selected'
                          }}"
                        aria-pressed={{if
                          (eq scenario.id this.selectedUserScenarioId)
                          "true"
                          "false"
                        }}
                        data-test-demo-user-scenario={{scenario.id}}
                        {{on "click" (fn this.selectUserScenario scenario.id)}}
                      >
                        <div class="font-semibold tio-fg-primary">{{scenario.name}}</div>
                        <div class="mt-1 text-sm tio-fg-muted">{{scenario.description}}</div>
                      </button>
                    {{/each}}
                  </div>
                </section>
              {{/if}}

              {{#if this.isTaProduct}}
                <section>
                  <h3 class="tio-text-sm-semibold uppercase tracking-wide tio-fg-muted">
                    TA payment model
                  </h3>
                  <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {{#each this.taPaymentModels as |model|}}
                      <button
                        type="button"
                        class="tio-option-card tio-option-card--compact tio-option-card--accent tio-accent-danger
                          {{if
                            (eq model.id this.selectedTaPaymentModelId)
                            'tio-option-card--selected'
                          }}"
                        aria-pressed={{if
                          (eq model.id this.selectedTaPaymentModelId)
                          "true"
                          "false"
                        }}
                        data-test-demo-ta-payment-model={{model.id}}
                        {{on "click" (fn this.selectTaPaymentModel model.id)}}
                      >
                        <div class="font-semibold tio-fg-primary">{{model.name}}</div>
                        <div class="mt-1 text-sm tio-fg-muted">{{model.description}}</div>
                      </button>
                    {{/each}}
                  </div>
                </section>
              {{/if}}

              {{#if this.isTaEmployeeExperience}}
                <section>
                  <h3 class="tio-text-sm-semibold uppercase tracking-wide tio-fg-muted">
                    TA scenario
                  </h3>
                  <p class="mt-1 text-xs tio-fg-disabled">
                    Pins the employee's TA dashboard to one application state. Automatic follows the
                    selected user scenario.
                  </p>
                  {{#if (eq this.selectedUserScenarioId "first-login")}}
                    <p class="mt-1 tio-text-xs-normal tio-fg-muted">
                      First Login has no applications yet. Choose another user scenario to show TA
                      progress.
                    </p>
                  {{/if}}
                  <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                    {{#each this.tasScenarioOptions as |scenario|}}
                      <button
                        type="button"
                        class="tio-option-card tio-option-card--compact tio-option-card--accent tio-accent-danger
                          {{if
                            (eq scenario.id this.selectedTasScenarioId)
                            'tio-option-card--selected'
                          }}"
                        aria-pressed={{if
                          (eq scenario.id this.selectedTasScenarioId)
                          "true"
                          "false"
                        }}
                        data-test-demo-ta-scenario={{if scenario.id scenario.id "auto"}}
                        disabled={{this.isTasScenarioDisabled scenario.id}}
                        aria-disabled={{if (this.isTasScenarioDisabled scenario.id) "true" "false"}}
                        {{on "click" (fn this.selectTasScenario scenario.id)}}
                      >
                        <div class="font-semibold tio-fg-primary">{{scenario.name}}</div>
                        <div class="mt-1 text-sm tio-fg-muted">{{scenario.description}}</div>
                      </button>
                    {{/each}}
                  </div>
                </section>
              {{/if}}

              {{#if this.isEmployeeExperience}}
                <section>
                  <h3 class="tio-text-sm-semibold uppercase tracking-wide tio-fg-muted">Debt</h3>
                  <p class="mt-1 text-xs tio-fg-disabled">
                    Sets the employee's linked-loan balance across the loan, SLR, and IDR surfaces.
                  </p>
                  {{#if this.debtScenarioNote}}
                    <p
                      class="mt-1 tio-text-xs-normal tio-fg-muted"
                      data-test-demo-debt-scenario-note
                    >
                      {{this.debtScenarioNote}}
                    </p>
                  {{/if}}
                  <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    {{#each this.debtLevels as |level|}}
                      <button
                        type="button"
                        disabled={{this.isDebtLevelDisabled level.id}}
                        aria-disabled={{if (this.isDebtLevelDisabled level.id) "true" "false"}}
                        class="tio-option-card tio-option-card--compact tio-option-card--accent tio-accent-brand
                          {{if (eq level.id this.selectedDebtLevelId) 'tio-option-card--selected'}}"
                        aria-pressed={{if (eq level.id this.selectedDebtLevelId) "true" "false"}}
                        data-test-demo-debt-level={{level.id}}
                        {{on "click" (fn this.selectDebtLevel level.id)}}
                      >
                        <div class="font-semibold tio-fg-primary">{{level.name}}</div>
                        <div class="mt-1 text-sm tio-fg-muted">{{level.description}}</div>
                      </button>
                    {{/each}}
                  </div>
                </section>
              {{/if}}

              <section>
                <h3 class="tio-text-sm-semibold uppercase tracking-wide tio-fg-muted">
                  Document status
                </h3>
                <p class="mt-1 tio-text-xs-normal tio-fg-disabled">
                  Sets the status the linked account statement and this session's uploads show once
                  read back. Existing NSLDS files stay applied. A successful upload can add your
                  first loan account.
                </p>
                <div class="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {{#each this.documentStatuses as |status|}}
                    <button
                      type="button"
                      class="tio-option-card tio-option-card--compact tio-option-card--accent tio-accent-info
                        {{if
                          (eq status.id this.selectedDocumentStatusId)
                          'tio-option-card--selected'
                        }}"
                      aria-pressed={{if
                        (eq status.id this.selectedDocumentStatusId)
                        "true"
                        "false"
                      }}
                      data-test-demo-document-status={{status.id}}
                      {{on "click" (fn this.selectDocumentStatus status.id)}}
                    >
                      <div class="tio-text-base-semibold tio-fg-primary">{{status.name}}</div>
                      <div class="mt-1 tio-text-sm-normal tio-fg-muted">{{status.description}}</div>
                    </button>
                  {{/each}}
                </div>
              </section>
            {{/unless}}
          </div>
        </:body>

        <:footer>
          <div class="tio-col">
            <div class="tio-col-tight">
              {{#if this.selectedStoryId}}
                <p class="tio-text-sm-normal tio-fg-muted">All settings included. Starts as the
                  employee at the benefit dashboard.</p>
              {{else}}
                <p class="text-sm tio-fg-muted">
                  Applying:
                  <span class="font-medium tio-fg-secondary">{{this.selectedClientName}}</span>
                  /
                  <span class="font-medium tio-fg-secondary">{{this.selectedProductNames}}</span>
                  /
                  <span class="font-medium tio-fg-secondary">{{this.selectedRoleName}}</span>
                  {{#if this.isEmployeeExperience}}
                    /
                    <span
                      class="font-medium tio-fg-secondary"
                    >{{this.selectedUserScenarioName}}</span>
                  {{/if}}
                  {{#if this.isTaProduct}}
                    /
                    <span
                      class="font-medium tio-fg-secondary"
                    >{{this.selectedTaPaymentModelName}}</span>
                  {{/if}}
                  {{#if this.selectedTasScenarioName}}
                    /
                    <span class="font-medium tio-fg-secondary">TA:
                      {{this.selectedTasScenarioName}}</span>
                  {{/if}}
                  {{#if this.isEmployeeExperience}}
                    /
                    <span class="font-medium tio-fg-secondary">{{this.selectedDebtLevelName}}</span>
                  {{/if}}
                  /
                  <span class="tio-text-sm-medium tio-fg-secondary">Documents:
                    {{this.selectedDocumentStatusName}}</span>
                </p>
                <p class="tio-text-xs-normal tio-fg-disabled" data-test-demo-apply-destination>
                  Applying opens the home page for the chosen role, so the first screen always
                  matches the selected products.
                </p>
              {{/if}}
            </div>
            <div class="tio-flex-end flex-wrap gap-3">
              {{#if this.canExitDemo}}
                <Button
                  @appearance="outlined"
                  @intent="danger"
                  @testHook="demo-options__exit"
                  class="shrink-0 whitespace-nowrap"
                  data-test-demo-exit-session
                  {{on "click" this.exitDemo}}
                >
                  Exit demo mode
                </Button>
              {{/if}}
              <Button
                @appearance="outlined"
                @intent="default"
                @testHook="demo-options__cancel"
                class="shrink-0 whitespace-nowrap"
                {{on "click" this.closeModal}}
              >
                Cancel
              </Button>
              <Button
                @intent="primary"
                @isDisabled={{and (not this.selectedStoryId) (not this.canApply)}}
                @testHook="demo-options__apply"
                class="shrink-0 whitespace-nowrap"
                data-test-demo-apply
                {{on "click" this.apply}}
              >
                {{if this.selectedStoryId "Start story" "Apply demo data"}}
              </Button>
            </div>
          </div>
        </:footer>
      </Modal>
      <Modal
        @isOpen={{this.isStoryHandoffOpen}}
        @onClose={{this.closeStoryHandoff}}
        @size="lg"
        @ariaLabel="Demo story role change"
        data-test-demo-story-handoff-modal
      >
        <:header><h2>{{this.storyAction.title}}</h2></:header>
        <:body>
          <p class="tio-text-base-normal tio-fg-primary">
            {{this.storyAction.description}}
          </p>
          <p class="mt-3 tio-text-sm-normal tio-fg-muted">This is a demo control. In the live app,
            employees and approvers use separate accounts; employees cannot approve their own
            applications. All story actions stay in this browser.</p>
        </:body>
        <:footer>
          <Button
            @appearance="outlined"
            @intent="default"
            @testHook="demo-story__stay-here"
            {{on "click" this.closeStoryHandoff}}
          >Stay here</Button>
          <Button
            @intent="primary"
            @testHook="demo-story__confirm-handoff"
            data-test-demo-story-handoff-confirm
            {{on "click" this.confirmStoryHandoff}}
          >{{this.storyAction.label}}</Button>
        </:footer>
      </Modal>
    {{/if}}
  </template>
}
